// #3383 — shown after a SAD save when the register reconcilers did not apply some
// of the agent's edits (or deliberately changed governed rows because the reply
// declared OVERRIDES). Before this the only trace was a server log line and the
// next render quietly restored the register's old text.

/** Mirrors RejectedEditReason (apps/api/src/lib/registerEditReport.ts); pinned by RegisterEditReportBanner.test.tsx (#3451). */
export type RegisterEditRejectReason =
  | 'governed' | 'low_overlap' | 'unknown_handle' | 'conflict' | 'not_authorised' | 'placeholder' | 'override_superseded' | 'possible_duplicate' | 'malformed_label';

export interface RegisterEditReportInfo {
  rejected: Array<{ handle: string; kind: 'risk' | 'open_question'; reason: RegisterEditRejectReason }>;
  overridden: Array<{ handle: string; kind: 'risk' | 'open_question' }>;
  /** True only when the agent's next-turn notice was actually recorded. */
  agentNotified?: boolean;
}

export const REASON_TEXT: Record<RegisterEditRejectReason, string> = {
  governed: 'already governed — its wording changes only if the agent declares OVERRIDES for it',
  low_overlap: 'the new text does not match the tracked item (it may have been added as a separate item)',
  unknown_handle: 'no such item in the register (it may have been added as a separate item)',
  not_authorised: 'the agent declared a change, but it could not be authorised: only the project PM or a tenant admin can authorise changing a governed item, and only when the change can be attributed to them',
  conflict: 'declared change could not be saved because the wording equals another item',
  placeholder: 'placeholder text, not a risk — not added',
  override_superseded: 'the agent declared a change for this item, but the text matches a different risk with the same number, so that risk was updated instead; this item is unchanged',
  malformed_label: 'a row labelled like this was not exactly R-<number>, so that row was ignored — nothing was added or changed',
  possible_duplicate: 'a bullet was very close to this question, so no new question was added and this one is unchanged',
};

// #3451 — a RISK refused as low_overlap is no longer added as a separate item (an open question still is).
const RISK_REASON_TEXT: Partial<Record<RegisterEditRejectReason, string>> = {
  low_overlap: 'the new text does not match the tracked risk, so it was not added — resend it under a new, unused R-number',
};

export function reasonText(reason: RegisterEditRejectReason, kind: 'risk' | 'open_question'): string {
  return (kind === 'risk' ? RISK_REASON_TEXT[reason] : undefined) ?? REASON_TEXT[reason] ?? 'not applied';
}

export function parseRegisterEditReportEvent(data: {
  type: string;
  rejected?: RegisterEditReportInfo['rejected'];
  overridden?: RegisterEditReportInfo['overridden'];
  agentNotified?: boolean;
}): RegisterEditReportInfo | null {
  if (data.type !== 'register_edit_report') return null;
  const rejected = Array.isArray(data.rejected) ? data.rejected : [];
  const overridden = Array.isArray(data.overridden) ? data.overridden : [];
  if (rejected.length === 0 && overridden.length === 0) return null;
  return { rejected, overridden, agentNotified: data.agentNotified === true };
}

export function RegisterEditReportBanner({ info }: { info: RegisterEditReportInfo }) {
  const hasRejected = info.rejected.length > 0;
  return (
    <div
      role={hasRejected ? 'alert' : 'status'}
      data-testid="register-edit-report-banner"
      className={
        hasRejected
          ? 'mx-6 mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex-shrink-0'
          : 'mx-6 mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex-shrink-0'
      }
    >
      {hasRejected && (
        <>
          <p className="font-semibold">The SAD was saved, but some risk / open-question edits were not applied to the register.</p>
          <p className="mt-1">
            Sections 10.1 and 10.2 are drawn from the register, so they still show its earlier text and may now disagree with the rest of the SAD.{info.agentNotified ? ' The agent has been told and will explain on its next reply.' : ' Tell the agent which items were not applied.'}
          </p>
          <ul className="mt-1 list-disc pl-5">
            {info.rejected.map((r) => (
              <li key={`${r.handle}-${r.reason}`}>
                <span className="font-mono">{r.handle}</span>: {reasonText(r.reason, r.kind)}
              </li>
            ))}
          </ul>
        </>
      )}
      {info.overridden.length > 0 && (
        <p className={hasRejected ? 'mt-2' : ''}>
          Wording updated in the register for declared overrides (status kept):{' '}
          <span className="font-mono">{info.overridden.map((o) => o.handle).join(', ')}</span>.
        </p>
      )}
    </div>
  );
}
